import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import NRDynamicJiraField from './NRDynamicJiraField';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import { buildJiraDynamicDefaultValues, formatJiraPayload, getDynamicJiraFields, getUnsupportedRequiredJiraFields } from './createJiraTicket.utils';

/**
 * Dynamic Jira fields section for issue creation.
 * Owns a react-hook-form instance for the schema-driven fields.
 */
const NRJiraDynamicForm = forwardRef(function NRJiraDynamicForm(
  { schema = [], disabled = false, onValidityChange, rpcOptions, onRefresh, isRefreshing = false },
  ref
) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const reactId = useId();
  const headerId = `${reactId}-header`;
  const panelId = `${reactId}-panel`;

  const fields = useMemo(() => getDynamicJiraFields(schema), [schema]);
  const unsupportedRequiredFields = useMemo(() => getUnsupportedRequiredJiraFields(schema), [schema]);
  const defaultValues = useMemo(() => buildJiraDynamicDefaultValues(fields), [fields]);
  const hasContent = fields.length > 0 || unsupportedRequiredFields.length > 0;

  const {
    control,
    reset,
    getValues,
    trigger,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  useEffect(() => {
    reset(defaultValues);
    // Re-validate so isValid reflects auto-selected defaults immediately.
    trigger();
  }, [defaultValues, reset, trigger]);

  useEffect(() => {
    onValidityChange?.(fields.length === 0 ? true : isValid);
  }, [fields.length, isValid, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => getValues(),
      trigger: () => trigger(),
      isValid: () => (fields.length === 0 ? true : isValid),
      getFormattedFields: async () => {
        if (fields.length === 0) {
          return [];
        }

        const isFormValid = await trigger();
        if (!isFormValid) {
          return null;
        }

        return formatJiraPayload(getValues(), fields);
      }
    }),
    [fields, getValues, isValid, trigger]
  );

  const toggleExpanded = () => setExpanded(current => !current);

  if (!hasContent) {
    return null;
  }

  return (
    <Box
      sx={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: 'transparent'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.5),
          width: '100%',
          px: theme.spacing(1),
          py: theme.spacing(0.5)
        }}
      >
        <ButtonBase
          id={headerId}
          type="button"
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}
          onClick={toggleExpanded}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: theme.spacing(1),
            flex: 1,
            minWidth: 0,
            px: theme.spacing(1),
            py: theme.spacing(1),
            textAlign: 'left',
            borderRadius: theme.spacing(1)
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.25), minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.primary">
              {CREATE_JIRA_TICKET_LABELS.REQUIRED_JIRA_FIELDS}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              {CREATE_JIRA_TICKET_LABELS.SYNCED_FROM_JIRA(fields.length)}
              {unsupportedRequiredFields.length > 0 && (
                <>
                  {'. '}
                  <Box component="span" sx={{ color: 'warning.main' }}>
                    {CREATE_JIRA_TICKET_LABELS.UNSUPPORTED_FIELDS_COUNT(unsupportedRequiredFields.length)}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        </ButtonBase>

        {onRefresh && (
          <Button
            type="button"
            variant="text"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={onRefresh}
            disabled={disabled || isRefreshing}
            aria-label={CREATE_JIRA_TICKET_LABELS.REFRESH_REQUIRED_FIELDS_ARIA}
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              typography: 'body2',
              textTransform: 'none',
              textDecoration: 'underline',
              minWidth: 'auto',
              px: theme.spacing(1),
              '& .MuiButton-startIcon': {
                marginRight: theme.spacing(0.5)
              },
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                color: 'text.secondary'
              },
              '&.Mui-disabled': {
                color: 'text.disabled'
              }
            }}
          >
            {CREATE_JIRA_TICKET_LABELS.REFRESH_REQUIRED_FIELDS}
          </Button>
        )}

        <ButtonBase
          type="button"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}
          onClick={toggleExpanded}
          sx={{
            flexShrink: 0,
            p: theme.spacing(0.75),
            borderRadius: '50%'
          }}
        >
          <ExpandMoreIcon
            aria-hidden
            sx={{
              color: theme.palette.text.secondary,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest
              })
            }}
          />
        </ButtonBase>
      </Box>

      {expanded && (
        <Box
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          sx={{
            px: theme.spacing(2),
            pb: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.5)
          }}
        >
          {unsupportedRequiredFields.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>{CREATE_JIRA_TICKET_LABELS.UNSUPPORTED_REQUIRED_FIELDS_TITLE}</AlertTitle>
              <Typography variant="body2" component="div">
                {CREATE_JIRA_TICKET_LABELS.UNSUPPORTED_REQUIRED_FIELDS_BODY}
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  mt: theme.spacing(1),
                  pl: theme.spacing(2.5)
                }}
              >
                {unsupportedRequiredFields.map(field => (
                  <Typography key={field.key} component="li" variant="body2">
                    {field.name || field.key}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          {fields.map(field => (
            <NRDynamicJiraField
              key={field.key}
              field={field}
              control={control}
              disabled={disabled}
              error={errors?.[field.key]}
              rpcOptions={rpcOptions}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

export default NRJiraDynamicForm;
